import type { CustomerServiceDefinition } from '../contracts/customer-services.js';

/**
 * The customer-facing design system.
 *
 * Deliberately not the admin theme: the admin panel is an operator tool, this is
 * a customer surface. What they share is only the discipline — tokens, one
 * spacing scale, one focus ring — not a single selector.
 *
 * ## Why the styles are a string, and why they live in the shadow root
 *
 * The widget is embedded in two sites that could not be more different: a
 * generated static Tamoomeh page and a 130-page hand-written Porabzar site with
 * its own `shared.css`. Any global stylesheet we shipped would eventually
 * collide with one of them. A shadow root gives hard style isolation in both
 * directions — our rules cannot leak out, and the host page's `*`/`button`
 * rules cannot leak in — while CSS custom properties still pierce the boundary
 * on purpose, which is how the widget picks up Porabzar's own surface colours.
 *
 * ## Theming
 *
 * Service identity (the accent) always comes from the shared service catalog, so
 * `vakil` is legal blue and `morabi` is teal on every host page. Neutral chrome
 * (surface, border, text, radius, input background) falls back to the host
 * page's tokens when `appearance="inherit"`, so the widget reads as part of
 * Porabzar rather than as a foreign box.
 */

/** Attribute values for the `appearance` attribute. */
export const APPEARANCE_MODES = ['neutral', 'inherit'] as const;
export type AppearanceMode = (typeof APPEARANCE_MODES)[number];

/** CSS custom properties a host page may set to theme the widget. */
export const THEME_VARIABLES = {
  accent: '--tamoomeh-accent',
  accentStrong: '--tamoomeh-accent-strong',
  accentSoft: '--tamoomeh-accent-soft',
  onAccent: '--tamoomeh-on-accent',
  radius: '--tamoomeh-radius',
  surface: '--tamoomeh-surface',
  surface2: '--tamoomeh-surface-2',
  border: '--tamoomeh-border',
  text: '--tamoomeh-text',
  textMuted: '--tamoomeh-text-muted',
  inputBg: '--tamoomeh-input-bg',
  font: '--tamoomeh-font',
  focus: '--tamoomeh-focus',
} as const;

/** Host-page tokens the widget reads when `appearance="inherit"`. */
const INHERITED_HOST_TOKENS = {
  radius: 'var(--radius, 12px)',
  surface: 'var(--panel, var(--surface, #ffffff))',
  surface2: 'var(--panel-2, var(--surface-2, #f6f7f9))',
  border: 'var(--panel-border, var(--border, rgba(16, 24, 40, 0.12)))',
  text: 'var(--text, #101828)',
  textMuted: 'var(--text-muted, #5c6670)',
  inputBg: 'var(--panel-2, var(--surface-2, #f6f7f9))',
} as const;

/**
 * Inline style attribute carrying a service's accent identity.
 *
 * Used by the element to paint one widget without touching any other, which is
 * what makes a page with two different service widgets possible at all.
 */
export function themeStyleFor(definition: CustomerServiceDefinition): string {
  const { theme } = definition;
  return [
    `${THEME_VARIABLES.accent}:${theme.accent}`,
    `${THEME_VARIABLES.accentStrong}:${theme.accentStrong}`,
    `${THEME_VARIABLES.accentSoft}:${theme.accentSoft}`,
    `${THEME_VARIABLES.onAccent}:${theme.onAccent}`,
  ].join(';');
}

/**
 * The component stylesheet.
 *
 * Everything is mobile-first: the base rules are the 320px layout and the only
 * media queries widen it. Nothing is fixed-width, so there is no horizontal
 * overflow at any of the target widths (320/375/390/414/768/1024/desktop).
 */
export const SERVICE_REQUEST_CSS = `
:host{
  /* Service identity — overwritten inline per widget. */
  --tamoomeh-accent:#1d4ed8;
  --tamoomeh-accent-strong:#1e3a8a;
  --tamoomeh-accent-soft:#eff4ff;
  --tamoomeh-on-accent:#ffffff;

  /* Neutral chrome — neutral by default, host tokens when asked. */
  --tamoomeh-radius:12px;
  --tamoomeh-surface:#ffffff;
  --tamoomeh-surface-2:#f6f7f9;
  --tamoomeh-border:rgba(16,24,40,.12);
  --tamoomeh-text:#101828;
  --tamoomeh-text-muted:#5c6670;
  --tamoomeh-input-bg:#ffffff;
  --tamoomeh-focus:var(--tamoomeh-accent);
  --tamoomeh-font:inherit;
  --tamoomeh-danger:#b42318;
  --tamoomeh-danger-soft:#fef3f2;
  --tamoomeh-success:#067647;
  --tamoomeh-success-soft:#ecfdf3;
  --tamoomeh-space:16px;

  display:block;
  box-sizing:border-box;
  max-width:100%;
  min-width:0;
  color:var(--tamoomeh-text);
  font-family:var(--tamoomeh-font);
  font-size:15px;
  line-height:1.7;
  text-align:start;
  direction:rtl;
}
:host([hidden]){display:none}
*,*::before,*::after{box-sizing:border-box}
button,input,select,textarea{font:inherit;color:inherit}

:host([appearance="inherit"]){
  --tamoomeh-radius:${INHERITED_HOST_TOKENS.radius};
  --tamoomeh-surface:${INHERITED_HOST_TOKENS.surface};
  --tamoomeh-surface-2:${INHERITED_HOST_TOKENS.surface2};
  --tamoomeh-border:${INHERITED_HOST_TOKENS.border};
  --tamoomeh-text:${INHERITED_HOST_TOKENS.text};
  --tamoomeh-text-muted:${INHERITED_HOST_TOKENS.textMuted};
  --tamoomeh-input-bg:${INHERITED_HOST_TOKENS.inputBg};
}

@media (prefers-color-scheme:dark){
  :host(:not([appearance="inherit"])){
    --tamoomeh-surface:#151a23;
    --tamoomeh-surface-2:#1b212c;
    --tamoomeh-border:rgba(232,236,240,.14);
    --tamoomeh-text:#e8ecf0;
    --tamoomeh-text-muted:#9aa3ad;
    --tamoomeh-input-bg:#1b212c;
  }
  /* The semantic surfaces are re-tokenised too, for every host including
     appearance="inherit". Otherwise the error state kept the light
     --tamoomeh-danger-soft (#fef3f2) and rendered a white box on a dark page. */
  :host{
    --tamoomeh-danger:#f97066;
    --tamoomeh-danger-soft:rgba(180,35,24,.22);
    --tamoomeh-success:#75e0a7;
    --tamoomeh-success-soft:rgba(6,118,71,.22);
  }
}
/* An explicit host theme wins over the OS preference. :host-context reads the
   data-theme the host page (Tamoomeh) sets on <html>, so the request form's
   error surface follows the visitor's chosen theme, not only the system one.
   These come after the media query so their higher specificity decides. */
:host-context([data-theme="light"]){
  --tamoomeh-danger:#b42318;
  --tamoomeh-danger-soft:#fef3f2;
  --tamoomeh-success:#067647;
  --tamoomeh-success-soft:#ecfdf3;
}
:host-context([data-theme="dark"]){
  --tamoomeh-danger:#f97066;
  --tamoomeh-danger-soft:rgba(180,35,24,.22);
  --tamoomeh-success:#75e0a7;
  --tamoomeh-success-soft:rgba(6,118,71,.22);
}

.panel{
  background:var(--tamoomeh-surface);
  border:1px solid var(--tamoomeh-border);
  border-radius:var(--tamoomeh-radius);
  padding:var(--tamoomeh-space);
  max-width:100%;
  min-width:0;
  overflow-wrap:anywhere;
}
:host([bare]) .panel{border:0;background:transparent;padding:0}

.head{display:flex;align-items:flex-start;gap:10px;min-width:0}
.head .icon{
  flex:0 0 auto;display:inline-grid;place-items:center;
  inline-size:40px;block-size:40px;border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-accent-soft);color:var(--tamoomeh-accent-strong);
}
.head .icon svg{inline-size:22px;block-size:22px;display:block}
.head .titles{min-width:0;flex:1 1 auto}
.head h2{margin:0;font-size:1.05rem;font-weight:800;letter-spacing:-.2px}
.head p{margin:4px 0 0;color:var(--tamoomeh-text-muted);font-size:.86rem}

.form{margin-top:14px;display:grid;gap:14px;min-width:0}
.field{display:grid;gap:6px;min-width:0}
/* A fieldset never shrinks below its content by default (min-inline-size:
   min-content): on a narrow phone the attachments group — whose bare file
   input has a wide intrinsic minimum — widened the whole page past 320px.
   Reset the minimum like every other container; border and padding stay at
   the UA default so the group's look does not change. Bare file inputs are
   capped the same way for the same reason. */
fieldset{min-inline-size:0;min-width:0;max-inline-size:100%}
input[type="file"]:not(.control){inline-size:100%;max-inline-size:100%;min-inline-size:0}
.field label{font-size:.86rem;font-weight:700}
.field .req{color:var(--tamoomeh-danger);margin-inline-start:3px}
.field .help{margin:0;font-size:.76rem;color:var(--tamoomeh-text-muted)}
/* The checkbox is the one field whose label sits beside its control, so it gets
   a full-width click target of its own instead of the stacked label/control
   shape. The native box stays visible for keyboard and screen-reader users. */
.field-check .check{
  display:flex;gap:9px;align-items:flex-start;font-size:.86rem;font-weight:700;
  cursor:pointer;min-block-size:44px;padding:8px 10px;
  border:1px solid var(--tamoomeh-border);border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-input-bg);
}
.field-check .check:hover{border-color:var(--tamoomeh-accent)}
.field-check .check:focus-within{outline:3px solid var(--tamoomeh-focus);outline-offset:2px}
.field-check input[type="checkbox"]{
  inline-size:20px;block-size:20px;margin:1px 0 0;flex:none;accent-color:var(--tamoomeh-accent);
}
.field-check .check span{padding-block-start:1px}
.control{
  inline-size:100%;max-width:100%;min-width:0;
  min-block-size:44px;padding:10px 12px;
  background:var(--tamoomeh-input-bg);color:var(--tamoomeh-text);
  border:1px solid var(--tamoomeh-border);border-radius:calc(var(--tamoomeh-radius) - 2px);
  transition:border-color .15s ease,box-shadow .15s ease;
}
textarea.control{min-block-size:112px;resize:vertical;line-height:1.7}
select.control{appearance:none;background-image:none}
.control:hover{border-color:var(--tamoomeh-accent)}
.control:focus-visible,.btn:focus-visible,.link:focus-visible,.otp input:focus-visible{
  outline:3px solid var(--tamoomeh-focus);outline-offset:2px;
}
.control[aria-invalid="true"]{border-color:var(--tamoomeh-danger);background:var(--tamoomeh-danger-soft)}
.control:disabled{opacity:.6;cursor:not-allowed}
/* A numeric field reads like a formatted column: Persian text stays right-to-left
   around it, the digits sit against the trailing edge. */
input[type="number"].control{text-align:end;font-variant-numeric:tabular-nums}
/* The native file control is the one input the platform does not draw itself;
   give its button the same shape as the widget's secondary actions. */
input[type="file"].control{padding:7px 10px;font-size:.82rem;line-height:1.6}
input[type="file"].control::file-selector-button{
  margin-inline-end:10px;padding:6px 12px;cursor:pointer;
  border:1px solid var(--tamoomeh-border);border-radius:calc(var(--tamoomeh-radius) - 4px);
  background:var(--tamoomeh-surface-2);color:var(--tamoomeh-text);font:inherit;font-weight:700;
}
input[type="file"].control::file-selector-button:hover{border-color:var(--tamoomeh-accent)}

.error{display:flex;gap:6px;align-items:flex-start;margin:0;font-size:.8rem;color:var(--tamoomeh-danger)}
.error::before{content:"⚠";line-height:1.7}

.actions{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-top:2px}
.btn{
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  min-block-size:44px;padding:0 18px;border-radius:calc(var(--tamoomeh-radius) - 2px);
  border:1px solid transparent;background:var(--tamoomeh-accent);color:var(--tamoomeh-on-accent);
  font-weight:700;font-size:.92rem;cursor:pointer;white-space:nowrap;
  transition:background .15s ease,opacity .15s ease;
}
.btn:hover{background:var(--tamoomeh-accent-strong)}
.btn:disabled{opacity:.65;cursor:progress}
.btn.secondary{background:transparent;color:var(--tamoomeh-accent-strong);border-color:var(--tamoomeh-border)}
.btn.secondary:hover{background:var(--tamoomeh-accent-soft)}
.btn.ghost{background:transparent;color:var(--tamoomeh-text-muted);border-color:transparent;padding-inline:10px}
.btn.ghost:hover{color:var(--tamoomeh-text);background:var(--tamoomeh-surface-2)}
/* The Pay call to action: the one unmistakable next step while a request is
   awaiting payment. It keeps the accent button language and adds a ring so it
   reads as primary even beside the secondary actions. */
.btn.pay{font-weight:800;padding-inline:24px;box-shadow:0 0 0 3px var(--tamoomeh-accent-soft)}
/* The payable amount, shown wherever the customer confirms a payment. The list
   price is struck through on its own row, the sale price leads the next row
   with the discount chip inline beside it, and the duration sits beneath —
   the same three-part price block the section pages use, restyled for the
   widget's compact cards and the card-transfer dialog. The DOM order never
   changes, so the figures a screen reader hears are the figures shown. */
.checkout-price{
  display:flex;flex-wrap:wrap;align-items:center;gap:2px 10px;min-inline-size:0;
  margin:0 0 10px;padding:10px 12px;border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-surface-2);border:1px solid var(--tamoomeh-border);
}
.checkout-price .price-list{
  flex:1 1 100%;min-inline-size:0;
  color:var(--tamoomeh-text-muted);font-size:.92rem;text-decoration:line-through;
}
.checkout-price .price-sale{
  order:2;
  font-size:clamp(1.25rem,1.1rem + 1vw,1.6rem);font-weight:800;line-height:1.45;
  color:var(--tamoomeh-accent-strong);
}
.checkout-price .price-off{
  order:3;display:inline-flex;align-items:center;padding:3px 10px;white-space:nowrap;
  border-radius:999px;background:var(--tamoomeh-accent);color:var(--tamoomeh-on-accent);
  font-size:.8rem;font-weight:800;
}
.checkout-price .price-duration{
  flex:1 1 100%;order:4;min-inline-size:0;display:block;font-size:.82rem;font-weight:600;
  color:var(--tamoomeh-text-muted);line-height:1.8;
}
/* In the card-transfer dialog the amount is the single most important number,
   so it sits above the card and centres to match the dialog's layout. */
.modal-host .checkout-price{align-items:center;justify-content:center;text-align:center}
/* Gateway-return feedback is a toast, not a paragraph in the flow.
   It describes something that happened outside the widget (a redirect back
   from the payment gateway), so it floats over the page, is unmistakably bold,
   and removes itself: it can never be read as a status label attached to
   whichever request happens to be on screen. */
.toast-host{
  position:fixed;inset-block-start:max(16px,env(safe-area-inset-top,0px));inset-inline:0;z-index:2147483000;
  display:flex;flex-direction:column;align-items:center;gap:10px;
  padding-inline:12px;pointer-events:none;
}
.pay-toast{
  pointer-events:auto;display:flex;align-items:flex-start;gap:12px;
  max-inline-size:min(92vw,480px);min-block-size:56px;padding:16px 18px;
  border:1px solid var(--tamoomeh-border);border-radius:calc(var(--tamoomeh-radius) - 2px);
  /* Opaque surface so long Persian lines never blend into page content. */
  background:rgb(255,255,255);color:#101828;
  box-shadow:0 16px 40px rgba(16,24,40,.22);
  font-size:1rem;line-height:1.75;animation:tamoomeh-toast-in .2s ease-out;
}
.pay-toast .pay-toast-dot{flex:0 0 auto;inline-size:10px;block-size:10px;margin-block-start:.55em;border-radius:50%;background:var(--tamoomeh-text-muted)}
.pay-toast .pay-toast-message{
  flex:1 1 auto;min-inline-size:0;font-weight:800;font-size:1rem;line-height:1.75;
  overflow-wrap:anywhere;word-break:break-word;white-space:pre-wrap;text-align:start;
}
.pay-toast .pay-toast-close{
  flex:0 0 auto;display:inline-grid;place-items:center;inline-size:32px;block-size:32px;
  padding:0;border:0;border-radius:50%;background:transparent;color:currentColor;
  font-size:1.1rem;line-height:1;opacity:.75;cursor:pointer;min-block-size:0;
}
.pay-toast .pay-toast-close:hover{opacity:1;background:rgba(16,24,40,.08)}
.pay-toast[data-tone="success"]{border-color:#16a34a;background:#ecfdf3;color:#065f46}
.pay-toast[data-tone="success"] .pay-toast-dot{background:#16a34a}
.pay-toast[data-tone="error"]{border-color:#b42318;background:#fef3f2;color:#b42318}
.pay-toast[data-tone="error"] .pay-toast-dot{background:#b42318}
.pay-toast[data-tone="info"]{border-color:var(--tamoomeh-accent);background:#eff6ff;color:#101828}
.pay-toast[data-tone="info"] .pay-toast-dot{background:var(--tamoomeh-accent)}
.pay-toast[data-leaving="1"]{opacity:0;transform:translateY(-8px);transition:opacity .18s ease,transform .18s ease}
:host-context([data-theme="dark"]) .pay-toast,
:host-context(:root:not([data-theme="light"])) .pay-toast{
  background:#111a2b;color:#e8ecf0;border-color:rgba(232,236,240,.18);
  box-shadow:0 16px 40px rgba(0,0,0,.45);
}
:host-context([data-theme="dark"]) .pay-toast[data-tone="success"],
:host-context(:root:not([data-theme="light"])) .pay-toast[data-tone="success"]{
  background:#052e1b;border-color:#166534;color:#4ade80;
}
:host-context([data-theme="dark"]) .pay-toast[data-tone="error"],
:host-context(:root:not([data-theme="light"])) .pay-toast[data-tone="error"]{
  background:#3b1213;border-color:#991b1b;color:#f87171;
}
:host-context([data-theme="dark"]) .pay-toast[data-tone="info"],
:host-context(:root:not([data-theme="light"])) .pay-toast[data-tone="info"]{
  background:#0f1b33;border-color:var(--tamoomeh-accent);color:#e8ecf0;
}
@keyframes tamoomeh-toast-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){
  .pay-toast{animation:none}
  .pay-toast[data-leaving="1"]{transition:none}
}
/* Card-transfer dialog (gateway maintenance). A bottom sheet on phones, a
   centred card from 560px up; every colour is a widget token, so the host's
   light/dark/system theme carries over without a dialog-specific palette. */
.modal-backdrop{
  position:fixed;inset:0;z-index:2147483001;
  display:flex;align-items:flex-end;justify-content:center;
  padding:12px;background:rgba(10,14,20,.55);
  animation:tamoomeh-fade-in .16s ease-out;
}
.modal{
  inline-size:100%;max-inline-size:440px;max-block-size:calc(100dvh - 24px);overflow:auto;
  display:grid;gap:14px;padding:18px;
  background:var(--tamoomeh-surface);color:var(--tamoomeh-text);
  border:1px solid var(--tamoomeh-border);border-radius:var(--tamoomeh-radius);
  box-shadow:0 24px 60px rgba(10,14,20,.35);
  direction:rtl;text-align:start;overflow-wrap:anywhere;
  animation:tamoomeh-sheet-in .2s ease-out;
}
.modal.modal-sheet-tall{max-inline-size:560px;max-block-size:min(92dvh,720px)}
.mine-sheet-body{min-block-size:0;overflow:auto;padding-block-end:4px}
.mine-sheet-body .mine{margin:0;padding:0;border:0;background:transparent;box-shadow:none}
.modal:focus{outline:none}
.modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}
.modal-head-text{min-inline-size:0;display:grid;gap:2px}
.modal-head h3{margin:0;font-size:1.02rem;font-weight:800;display:flex;align-items:center;gap:8px}
.modal-head h3 svg{color:var(--tamoomeh-accent-strong)}
.modal-sub{margin:0;font-size:.8rem;color:var(--tamoomeh-text-muted)}
/* The sheet's dismiss control. A 48×48 target well above the 44px minimum, a
   visible circular surface so the control is not lost against the sheet, and a
   real 22px close glyph at full contrast. Only the surface is tinted between
   states — the icon itself never loses contrast, and its size is fixed rather
   than inherited, so a host page's font-size cannot shrink it. The header is a
   flex row with space-between, so the button stays pinned to the inline-end
   (top-left in RTL) and can never overlap the title, which is the only
   flexible child; the negative margin keeps it inside the sheet padding
   without inflating the header's height. */
.modal-close{
  flex:0 0 auto;display:inline-grid;place-items:center;inline-size:48px;block-size:48px;
  margin:-10px;padding:0;border:0;border-radius:50%;
  background:var(--tamoomeh-surface-2);color:var(--tamoomeh-text);cursor:pointer;
}
.modal-close svg{inline-size:22px;block-size:22px;pointer-events:none}
.modal-close:hover{background:var(--tamoomeh-border);color:var(--tamoomeh-text)}
.modal-close:focus-visible{outline:3px solid var(--tamoomeh-focus);outline-offset:2px}
.modal-message{
  margin:0;padding:12px;border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-accent-soft);color:var(--tamoomeh-text);font-weight:700;font-size:.9rem;
  border:1px solid var(--tamoomeh-border);
}
.card-box{
  display:grid;gap:4px;padding:14px;border-radius:calc(var(--tamoomeh-radius) - 2px);
  border:1px dashed var(--tamoomeh-accent);background:var(--tamoomeh-surface-2);
}
.card-box .card-label{font-size:.78rem;color:var(--tamoomeh-text-muted);font-weight:700}
.card-box .card-number{
  font-size:1.28rem;font-weight:800;letter-spacing:1.5px;font-variant-numeric:tabular-nums;
  direction:ltr;text-align:center;unicode-bidi:isolate;user-select:all;-webkit-user-select:all;
}
.card-box .card-holder{font-size:.84rem;color:var(--tamoomeh-text-muted);text-align:center}
.modal-actions{display:grid;gap:10px}
.modal-actions .btn{inline-size:100%}
.modal-actions .btn[data-copy-state="copied"]{background:var(--tamoomeh-success);border-color:var(--tamoomeh-success);color:#fff}
.modal-actions .btn[aria-busy="true"]{opacity:.75;cursor:progress}
.modal-feedback{margin:0;min-block-size:1.4em;font-size:.82rem;font-weight:700;color:var(--tamoomeh-text-muted)}
.modal-feedback[data-tone="ok"]{color:var(--tamoomeh-success)}
.modal-feedback[data-tone="error"]{color:var(--tamoomeh-danger)}
.modal-note{margin:0;font-size:.78rem;color:var(--tamoomeh-text-muted)}
@media (min-width:560px){
  .modal-backdrop{align-items:center}
  .modal{padding:22px}
  .modal-actions{grid-template-columns:1fr 1fr}
  .modal-actions .btn.ghost{grid-column:1 / -1}
}
@keyframes tamoomeh-fade-in{from{opacity:0}to{opacity:1}}
@keyframes tamoomeh-sheet-in{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){
  .modal-backdrop,.modal{animation:none}
}
.link{
  background:none;border:0;padding:4px 2px;min-block-size:32px;
  color:var(--tamoomeh-accent-strong);text-decoration:underline;cursor:pointer;font-size:.84rem;
}

.spinner{
  inline-size:16px;block-size:16px;flex:0 0 auto;border-radius:50%;
  border:2px solid currentColor;border-block-start-color:transparent;
  animation:tamoomeh-spin .7s linear infinite;
}
@keyframes tamoomeh-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){
  .spinner{animation-duration:2s}
  *{transition:none !important}
}

.status{
  display:flex;gap:10px;align-items:flex-start;margin-top:14px;
  padding:12px;border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-surface-2);border:1px solid var(--tamoomeh-border);
  font-size:.86rem;min-width:0;
}
.status .dot{flex:0 0 auto;inline-size:10px;block-size:10px;border-radius:50%;margin-block-start:7px;background:var(--tamoomeh-text-muted)}
.status[data-tone="error"]{background:var(--tamoomeh-danger-soft);border-color:var(--tamoomeh-danger)}
.status[data-tone="error"] .dot{background:var(--tamoomeh-danger)}
.status[data-tone="success"]{background:var(--tamoomeh-success-soft)}
.status[data-tone="success"] .dot{background:var(--tamoomeh-success)}
.status .body{min-width:0}
.status strong{display:block;font-weight:800}
.status p{margin:2px 0 0;color:var(--tamoomeh-text-muted)}

/* One real input, not a row of styled boxes: the label, the hint and the error
   are all text, so the control is usable with a screen reader and with CSS
   disabled. inputmode + autocomplete give the numeric keypad and SMS
   autofill without hiding the field behind styling. */
.otp{display:grid;gap:8px;justify-items:center}
.otp input{
  inline-size:100%;max-inline-size:220px;block-size:56px;text-align:center;
  font-size:1.5rem;font-weight:700;letter-spacing:.5em;text-indent:.5em;
  /* Isolate from the RTL host: without this, digits can render reversed while
     the underlying value stays LTR, so the customer types the SMS code
     "backwards" to match what they see and verification fails. */
  direction:ltr;unicode-bidi:isolate;font-variant-numeric:tabular-nums;
  background:var(--tamoomeh-input-bg);color:var(--tamoomeh-text);
  border:1px solid var(--tamoomeh-border);border-radius:calc(var(--tamoomeh-radius) - 2px);
}
.otp input[aria-invalid="true"]{border-color:var(--tamoomeh-danger);background:var(--tamoomeh-danger-soft)}
.otp .hint{font-size:.78rem;color:var(--tamoomeh-text-muted);text-align:center;margin:0}
.otp .hint .phone-display{direction:ltr;unicode-bidi:isolate;display:inline-block}

.phone-row{display:flex;gap:8px;align-items:stretch;min-width:0}
/* text-align:left (not start): start follows the RTL host and right-aligns an
   LTR tel field, which makes Iranian numbers appear to run backwards. */
.phone-row .control{
  flex:1 1 auto;min-width:0;direction:ltr;unicode-bidi:isolate;text-align:left;
}
.auth-chooser{display:grid;gap:14px;margin-top:4px}
.auth-chooser-lead{margin:0;font-size:.88rem;color:var(--tamoomeh-text-muted)}
.auth-method-picker{
  display:grid;gap:12px;padding:14px;
  border:1px solid var(--tamoomeh-border);border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-surface-2);
}
.auth-method{
  inline-size:100%;justify-content:flex-start;text-align:start;gap:12px;
  min-block-size:56px;padding-block:12px;white-space:normal;
}
.auth-method svg{flex:0 0 auto}
.auth-method-copy{display:grid;gap:2px;min-inline-size:0}
.auth-method-copy strong{font-size:.95rem;font-weight:800;line-height:1.3}
.auth-method-copy span{font-size:.78rem;font-weight:500;opacity:.85}
.auth-method-google{background:var(--tamoomeh-accent);color:var(--tamoomeh-on-accent);font-weight:800}
.auth-method-google .auth-method-copy strong{font-weight:900}
.auth-method-sms{background:var(--tamoomeh-surface);color:var(--tamoomeh-text)}
.auth-method-sms:hover{background:var(--tamoomeh-surface)}
.phone-row .control,.otp .control{scroll-margin-block:24px}
.auth-phone-box .actions,.form[data-action="verify-otp"] .actions{scroll-margin-block-end:24px}
.auth-method-or{
  display:flex;align-items:center;gap:10px;
  color:var(--tamoomeh-text-muted);font-size:.85rem;font-weight:700;
}
.auth-method-or::before,.auth-method-or::after{
  content:"";flex:1 1 auto;border-block-start:1px solid var(--tamoomeh-border);
}
.auth-method-or span{flex:0 0 auto}
.auth-chooser-back{margin-top:0}
.auth-phone-box{position:relative}
.auth-phone-toolbar{
  display:flex;justify-content:flex-start;margin:0 0 8px;
  /* In RTL, flex-start is the inline-start (right). Place «بازگشت» at the
     visual top-left by pinning it to inline-end. */
  justify-content:flex-end;
}
.auth-phone-heading{margin:0 0 6px;font-size:1.05rem;font-weight:800;letter-spacing:-.2px}
.auth-phone-lead{margin:0 0 14px}
.auth-back{min-block-size:36px;padding-inline:8px;font-weight:600}
.btn .spinner{margin-inline-end:2px}
@media (min-width:520px){
  .auth-method-picker{
    grid-template-columns:1fr auto 1fr;align-items:stretch;gap:14px;
  }
  .auth-method-or{
    flex-direction:column;justify-content:center;align-self:stretch;
    inline-size:2.2rem;margin:0;gap:8px;
  }
  .auth-method-or::before,.auth-method-or::after{
    border-block-start:0;border-inline-start:1px solid var(--tamoomeh-border);
    flex:1 1 auto;inline-size:0;min-block-size:12px;align-self:center;
  }
}
.meta{font-size:.78rem;color:var(--tamoomeh-text-muted);margin:0}
.visually-hidden{
  position:absolute !important;inline-size:1px;block-size:1px;padding:0;margin:-1px;
  overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;
}
.sr-only-live{position:absolute;inline-size:1px;block-size:1px;overflow:hidden;clip:rect(0 0 0 0)}

.attachments{
  margin:14px 0 0;padding:12px 14px;border:1px solid var(--tamoomeh-border);
  border-radius:calc(var(--tamoomeh-radius) - 2px);background:var(--tamoomeh-surface-2);
  display:grid;gap:10px;min-width:0;
}
.attachments legend{padding:0 4px;font-weight:800;font-size:.92rem}
.attachment-list{list-style:none;margin:0;padding:0;display:grid;gap:8px}
.attachment-item{
  display:flex;flex-wrap:wrap;gap:8px 12px;align-items:center;
  padding:8px 10px;border-radius:8px;background:var(--tamoomeh-surface);
  border:1px solid var(--tamoomeh-border);min-width:0;
}
.attachment-name{flex:1 1 12ch;min-inline-size:0;font-weight:700;overflow-wrap:anywhere}
.attachment-size{font-size:.82rem}
.attachment-remove{min-block-size:0;padding:4px 10px;font-size:.82rem}

.receipt{margin-top:12px;display:grid;gap:6px;font-size:.86rem}
.receipt .row{display:flex;flex-wrap:wrap;gap:6px;justify-content:space-between;min-width:0}
.receipt .row dt{color:var(--tamoomeh-text-muted)}
.receipt .row dd{margin:0;font-weight:700}
.receipt .code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;direction:ltr;unicode-bidi:isolate}

/* ---- "درخواست‌های من" — the customer's own requests as a timeline ---------
   A list of cards, each one a details element: the summary is the request
   number, the current stage and the date; opening it reveals the explanation,
   the progress track and the reference table. The native details element is
   used rather than a scripted accordion because it is keyboard- and
   screen-reader-navigable with no JavaScript at all, which is the same standard
   the form holds itself to. */
.mine{margin-top:22px;padding-top:18px;border-top:1px solid var(--tamoomeh-border);min-width:0}
.mine-head{display:grid;gap:4px}
.mine-head h3{margin:0;font-size:1rem;font-weight:800}
.mine-head p{margin:0;font-size:.82rem;color:var(--tamoomeh-text-muted)}
.mine-note{margin:12px 0 0;font-size:.84rem;color:var(--tamoomeh-text-muted)}
.mine-empty{
  margin-top:12px;display:grid;gap:6px;justify-items:center;text-align:center;
  padding:20px 14px;border:1px dashed var(--tamoomeh-border);
  border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-surface-2);color:var(--tamoomeh-text-muted);font-size:.84rem;
}
.mine-empty p{margin:0}
.mine-empty strong{color:var(--tamoomeh-text)}
.mine-empty-icon{color:var(--tamoomeh-accent-strong)}

.mine-list{list-style:none;margin:14px 0 0;padding:0;display:grid;gap:10px}
.req{min-width:0}
.req details{
  border:1px solid var(--tamoomeh-border);
  border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-surface-2);overflow:hidden;
}
.req[data-stage="awaiting_payment"] details{border-color:var(--tamoomeh-accent)}
.req summary{
  display:flex;flex-wrap:wrap;gap:4px 10px;align-items:center;
  padding:12px;cursor:pointer;min-block-size:44px;list-style:none;
}
.req summary::-webkit-details-marker{display:none}
.req summary:focus-visible{outline:3px solid var(--tamoomeh-focus);outline-offset:-3px}
.req-ref{font-size:.8rem;font-weight:700;color:var(--tamoomeh-text-muted)}
/* The request's own service type, as one compact chip: the section's icon plus
   its Persian name, from the same shared catalog entry the request was filed
   against. An auto inline-start margin pushes it to the inline-end of the
   summary row — the physical top-left in this RTL layout — so it never competes
   with the reference and the stage, while a fixed flex basis and nowrap keep it
   from colliding with them or breaking a label mid-word. The full inline-size
   ceiling keeps the chip inside the card on a narrow phone, never overflowing
   it. */
.req-svc{
  display:inline-flex;align-items:center;gap:5px;flex:0 0 auto;
  margin-inline-start:auto;max-inline-size:100%;
  padding-block:2px;padding-inline:9px;border-radius:999px;
  border:1px solid var(--tamoomeh-accent);background:var(--tamoomeh-accent-soft);
  color:var(--tamoomeh-accent-strong);font-size:.76rem;font-weight:700;line-height:1.7;
  white-space:nowrap;
}
.req-svc-icon{display:inline-flex;flex:0 0 auto}
.req-svc-icon svg{inline-size:14px;block-size:14px}
/* Service-specific chip colours, keyed by the canonical customer-service slug
   on data-service — never by titles, paths or localized text. The values
   mirror the one shared service palette (site service-brand.ts): legal
   blue, sports lacquer red, child green. Light values pair a deep text colour
   with a pale tint for contrast; the dark overrides do the reverse. */
.req-svc[data-service="vakil"]{
  border-color:#1d4ed8;background:#eff4ff;color:#1e3a8a;
}
.req-svc[data-service="morabi"]{
  border-color:#b91c1c;background:#fef2f2;color:#7f1d1d;
}
.req-svc[data-service="koodak"]{
  border-color:#15803d;background:#f0fdf4;color:#166534;
}
@media (prefers-color-scheme:dark){
  .req-svc[data-service="vakil"]{
    border-color:#7ea2ff;background:rgba(29,78,216,.24);color:#a9c1ff;
  }
  .req-svc[data-service="morabi"]{
    border-color:#f87171;background:rgba(185,28,28,.26);color:#fca5a5;
  }
  .req-svc[data-service="koodak"]{
    border-color:#4ade80;background:rgba(21,128,61,.26);color:#86efac;
  }
}
/* The completed-requests disclosure: collapsed by default (no open attribute
   in the markup). The summary is a native keyboard control with a visible
   focus ring; the caret rotates while open. */
.mine-completed{
  margin-top:12px;border:1px solid var(--tamoomeh-border);
  border-radius:calc(var(--tamoomeh-radius) - 2px);
  background:var(--tamoomeh-surface-2);overflow:hidden;
}
.mine-completed > summary{
  display:flex;align-items:center;gap:8px;cursor:pointer;
  padding:12px;min-block-size:44px;list-style:none;
  font-weight:800;font-size:.9rem;
}
.mine-completed > summary::-webkit-details-marker{display:none}
.mine-completed > summary:focus-visible{outline:3px solid var(--tamoomeh-focus);outline-offset:-3px}
.mine-completed[open] .req-caret{transform:rotate(180deg)}
.mine-completed-count{
  display:inline-flex;align-items:center;justify-content:center;
  min-inline-size:26px;padding-block:1px;padding-inline:8px;border-radius:999px;
  background:var(--tamoomeh-accent-soft);color:var(--tamoomeh-accent-strong);
  font-size:.78rem;font-weight:800;line-height:1.7;
}
.mine-completed .mine-list{padding:0 12px 12px}
/* Message attachments: name, preview and download. Previews never exceed the
   card; images keep their aspect ratio and PDFs get a fixed frame. */
.req-attachments{list-style:none;margin:8px 0 0;padding:0;display:grid;gap:8px}
.req-attachment{
  display:grid;gap:6px;padding:8px 10px;border:1px solid var(--tamoomeh-border);
  border-radius:10px;background:var(--tamoomeh-surface);overflow-wrap:anywhere;
}
.req-attachment-name{font-size:.82rem;font-weight:700}
.req-attachment-preview img{
  display:block;max-inline-size:100%;block-size:auto;max-block-size:320px;
  object-fit:contain;border-radius:8px;
}
.req-attachment-pdf{inline-size:100%;block-size:320px;border:0;border-radius:8px;background:var(--tamoomeh-surface)}
@media (max-width:480px){
  .req-attachment-pdf{block-size:240px}
}
/* A pending-payment request shows its reference as a chip, so the request code
   itself — not only the stage line — carries the "you still owe money" signal.
   The recipe mirrors the site's .chip (soft tint, strong text, hairline
   border, pill radius) but reads the widget's own accent tokens, so it follows
   the service theme (vakil blue, morabi teal, …) and dark mode with no extra
   rules. The reference stays LTR-isolated inside the RTL card, and nowrap
   keeps a code from breaking mid-token on narrow screens. */
.req-ref.req-chip{
  display:inline-flex;align-items:center;flex:0 0 auto;
  padding-block:2px;padding-inline:10px;
  border:1px solid var(--tamoomeh-accent);
  border-radius:999px;
  background:var(--tamoomeh-accent-soft);
  color:var(--tamoomeh-accent-strong);
  font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;
  font-size:.78rem;font-weight:800;letter-spacing:.02em;line-height:1.6;
  direction:ltr;unicode-bidi:isolate;white-space:nowrap;
}
.req-stage{font-size:.9rem;font-weight:800;color:var(--tamoomeh-text)}
.req[data-stage="unknown"] .req-stage{font-weight:700;color:var(--tamoomeh-text-muted)}
.req-date{font-size:.78rem;color:var(--tamoomeh-text-muted);margin-inline-start:auto}
.req-caret{display:inline-flex;color:var(--tamoomeh-text-muted);transition:transform .18s ease}
.req details[open] .req-caret{transform:rotate(180deg)}

.req-body{padding:0 12px 14px;display:grid;gap:12px;min-width:0}
.req-explain{margin:0;font-size:.86rem;color:var(--tamoomeh-text-muted)}
.req-hint{margin:0;font-size:.78rem;color:var(--tamoomeh-text-muted)}

/* The progress track. The current step is the only one with a filled, ringed
   dot and full-contrast text, so "where am I" is never colour-only: the current
   step also carries aria-current="step". */
.req-track{list-style:none;margin:0;padding:0;display:grid;gap:0}
.req-track li{
  position:relative;display:grid;grid-template-columns:18px minmax(0,1fr);
  align-items:start;gap:10px;padding-block:6px;
  font-size:.82rem;color:var(--tamoomeh-text-muted);
}
.req-track li:not(:last-child)::before{
  content:"";position:absolute;inset-inline-start:8px;inset-block:22px 0;
  inline-size:2px;background:var(--tamoomeh-border);
}
.req-dot{
  position:relative;z-index:1;inline-size:18px;block-size:18px;border-radius:50%;
  border:2px solid var(--tamoomeh-border);background:var(--tamoomeh-surface);
  display:inline-flex;align-items:center;justify-content:center;
  font-size:11px;line-height:1;color:#fff;
}
.req-track li[data-state="done"] .req-dot{
  background:#15803d;border-color:#15803d;
}
.req-track li[data-state="done"] .req-step-title{color:var(--tamoomeh-text);font-weight:700}
.req-track li[data-state="current"] .req-dot{
  background:var(--tamoomeh-accent);border-color:var(--tamoomeh-accent);
}
.req-track li[data-state="current"] .req-dot{box-shadow:0 0 0 4px var(--tamoomeh-accent-soft)}
.req-track li[data-state="current"] .req-step-title{color:var(--tamoomeh-text);font-weight:800}
.req-check{font-weight:900}
@media (max-width:480px){
  .req-track li{font-size:.78rem;padding-block:5px}
}
.req-step-title{min-width:0;overflow-wrap:anywhere}

.req-secondary{margin-block-start:10px;border:1px solid var(--tamoomeh-border);border-radius:var(--tamoomeh-radius-sm);padding:8px 10px;background:var(--tamoomeh-surface-2)}
.req-secondary > summary{cursor:pointer;font-weight:700;font-size:.86rem;min-block-size:36px;display:flex;align-items:center}
.req-secondary[open] > summary{margin-block-end:8px}
.req-meta{display:grid;gap:6px;margin:0;font-size:.82rem}
.req-meta > div{display:flex;flex-wrap:wrap;gap:6px;justify-content:space-between;min-width:0}
.req-meta dt{color:var(--tamoomeh-text-muted)}
.req-meta dd{margin:0;font-weight:700;min-width:0;overflow-wrap:anywhere}
.req-meta .code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;direction:ltr;unicode-bidi:isolate}

/* What the expert sent back: messages and deliverables, inside the request's
   detail. A quiet inset surface keeps them visually part of the request rather
   than competing with the request's own stage and summary. */
.req-messages,.req-deliverables{
  margin:0;padding:10px 12px;border:1px solid var(--tamoomeh-border);
  border-radius:12px;background:var(--tamoomeh-surface-2);
}
.req-sub{margin:0 0 8px;font-size:.84rem;font-weight:800;color:var(--tamoomeh-text)}
.req-messages ul,.req-deliverables ul{list-style:none;margin:0;padding:0;display:grid;gap:10px}
.req-messages li,.req-deliverables li{
  display:grid;gap:4px;padding-inline-start:10px;border-inline-start:2px solid var(--tamoomeh-accent-soft);
}
.req-messages li p{margin:0;font-size:.85rem;line-height:1.9;overflow-wrap:anywhere}
.req-program{margin:0;font-size:.85rem;line-height:1.9;white-space:pre-wrap;overflow-wrap:anywhere}
.req-messages time,.req-deliverables time{font-size:.74rem;color:var(--tamoomeh-text-muted)}

/* The manual receipt fallback. A disclosure, so it never competes with the Pay
   button, holding a real file + description form. It is the gateway-unavailable
   path: a customer uploads «رسید پرداخت» and an expert reviews it. */
.receipt-upload{
  margin-top:10px;padding:10px 12px;border:1px dashed var(--tamoomeh-border);
  border-radius:12px;background:var(--tamoomeh-surface-2);
}
.receipt-upload[open]{padding-bottom:14px}
.receipt-summary{
  display:flex;align-items:center;gap:8px;cursor:pointer;list-style:none;
  font-size:.86rem;font-weight:800;color:var(--tamoomeh-text);
}
.receipt-summary::-webkit-details-marker{display:none}
.receipt-hint{margin:8px 0 0;font-size:.78rem;color:var(--tamoomeh-text-muted);line-height:1.8}
.receipt-form{display:grid;gap:10px;margin-top:10px}
.receipt-form input[type="file"],
.receipt-form textarea{
  inline-size:100%;padding:9px 10px;border:1px solid var(--tamoomeh-border);
  border-radius:10px;background:var(--tamoomeh-input-bg);color:var(--tamoomeh-text);
  font:inherit;font-size:.85rem;
}
.receipt-form textarea{resize:vertical;line-height:1.7}
.ok{display:flex;gap:6px;align-items:flex-start;margin:8px 0 0;font-size:.8rem;color:var(--tamoomeh-success)}
.ok::before{content:"✓";line-height:1.7}

/* iOS Safari zooms the whole page when a text field under 16px takes focus and
   does not zoom back out, leaving it wider than the screen and panned sideways.
   The sign-in step focuses the phone input on arrival, so on touch screens every
   text-entry control is at least 16px (the code input is already larger). */
@media (pointer:coarse){
  .control:not([type="file"]),
  .receipt-form textarea{font-size:max(16px,1em)}
}

/* Two columns only where there is genuinely room; never a fixed width. */
@media (min-width:768px){
  :host{--tamoomeh-space:20px}
  .form.grid-2{grid-template-columns:repeat(2,minmax(0,1fr))}
  .form.grid-2 .span-2{grid-column:1 / -1}
}
`;

/** Expose the neutral palette so a host page can pre-seed the variables. */
export function neutralThemeVariables(): Record<string, string> {
  return {
    [THEME_VARIABLES.radius]: '12px',
    [THEME_VARIABLES.surface]: '#ffffff',
    [THEME_VARIABLES.surface2]: '#f6f7f9',
    [THEME_VARIABLES.border]: 'rgba(16,24,40,.12)',
    [THEME_VARIABLES.text]: '#101828',
    [THEME_VARIABLES.textMuted]: '#5c6670',
    [THEME_VARIABLES.inputBg]: '#ffffff',
  };
}
